/* eslint-disable complexity */
import { FormattedMessage } from '@futrli/localisation';
import { createLengthValidator, createPasswordValidator, validateEmailAddress } from '@futrli/shared-services';
import React from 'react';
import { IntlShape } from 'react-intl';

import { FORM_FIELDS, REGISTER_FORM_ID, FORGOT_PASSWORD_SUBMIT_FORM_ID, INVITE_FORM_ID } from '../core/constants';

/**
 * Forms that allow us to create/update user properties
 */
const EDIT_FORMS = [REGISTER_FORM_ID, FORGOT_PASSWORD_SUBMIT_FORM_ID, INVITE_FORM_ID];

/**
 * Returns the validation function for a form field.
 */
function getFieldValidator(intl?: IntlShape) {
  const MAX_NAME_LENGTH = 100;
  const MIN_PHONE_NUMBER_LENGTH = 10;
  const nameValidator = createLengthValidator(MAX_NAME_LENGTH);
  const passwordValidator = createPasswordValidator();

  return (fieldKey: string, formValues: Record<string, string>, formId: string) => {
    const fieldValue = formValues[fieldKey];

    if (!fieldValue?.length) {
      return intl ? (
        intl.formatMessage({ id: 'validation.field_required', defaultMessage: 'This field is required.' })
      ) : (
        <FormattedMessage id="validation.field_required" defaultMessage="This field is required." />
      );
    }

    switch (fieldKey) {
      case FORM_FIELDS.businessPhoneNumber:
        if (fieldValue.length < MIN_PHONE_NUMBER_LENGTH) {
          return intl ? (
            intl.formatMessage({
              id: 'validation.invalid_phone_number',
              defaultMessage: 'Please make sure you have entered a valid phone number.'
            })
          ) : (
            <FormattedMessage
              id="validation.invalid_phone_number"
              defaultMessage="Please make sure you have entered a valid phone number."
            />
          );
        }
      case FORM_FIELDS.name:
        return !nameValidator(fieldValue) ? (
          intl ? (
            intl.formatMessage({
              id: 'validation.only_accept_100_char',
              defaultMessage: 'Oops, we can only accept 100 characters here.'
            })
          ) : (
            <FormattedMessage
              id="validation.only_accept_100_char"
              defaultMessage="Oops, we can only accept 100 characters here."
            />
          )
        ) : undefined;
      case FORM_FIELDS.username:
        if (!nameValidator(fieldValue)) {
          return intl ? (
            intl.formatMessage({
              id: 'validation.only_accept_100_char',
              defaultMessage: 'Oops, we can only accept 100 characters here.'
            })
          ) : (
            <FormattedMessage
              id="validation.only_accept_100_char"
              defaultMessage="Oops, we can only accept 100 characters here."
            />
          );
        } else if (!validateEmailAddress(fieldValue)) {
          return intl ? (
            intl.formatMessage({
              id: 'validation.invalid_email',
              defaultMessage: 'Please make sure you have entered a valid email address.'
            })
          ) : (
            <FormattedMessage
              id="validation.invalid_email"
              defaultMessage="Please make sure you have entered a valid email address."
            />
          );
        }
        return;

      case FORM_FIELDS.password:
        if (!EDIT_FORMS.includes(formId) || passwordValidator(fieldValue).isValid) {
          return;
        }

        return intl ? (
          intl.formatMessage({
            id: 'validation.invalid_password_description',
            defaultMessage:
              'Sorry, your password must be at least 9 or more characters and contain a capital letter, number and a symbol.'
          })
        ) : (
          <FormattedMessage
            id="validation.invalid_password_description"
            defaultMessage="Sorry, your password must be at least 9 or more characters and contain a capital letter, number and a symbol."
          />
        );

      case FORM_FIELDS.confirmPassword:
        if (!EDIT_FORMS.includes(formId) || fieldValue === formValues.password) {
          return;
        }
        return intl ? (
          intl.formatMessage({ id: 'validation.passwords_do_not_match', defaultMessage: 'Passwords do not yet match.' })
        ) : (
          <FormattedMessage id="validation.passwords_do_not_match" defaultMessage="Passwords do not yet match." />
        );

      default:
        return;
    }
  };
}

/**
 * Redux Form validator
 * @param {(keyof typeof FORM_FIELDS)[]} requiredFields
 * @returns form errors for required fields.
 */
export function getFormValidator(requiredFields: (keyof typeof FORM_FIELDS)[], formId: string, intl?: IntlShape) {
  const validateField = getFieldValidator(intl);

  return (formValues: Record<string, string>) => {
    // Check required fields needed as ReduxForm does not return the fields with empty value in formValues callback param.
    // eslint-disable-next-line space-before-function-paren
    const errors = requiredFields.reduce(function (memo, fieldKey) {
      if (!formValues[fieldKey]) {
        memo[fieldKey] = <FormattedMessage id="validation.field_required" defaultMessage="This field is required." />;
      }
      return memo;
    }, {} as Record<string, string | JSX.Element>);

    // Validate non empty fields - formValues coming from ReduxForm
    return Object.keys(formValues).reduce((memo, fieldKey) => {
      const error = validateField(fieldKey, formValues, formId);

      if (error) {
        memo[fieldKey] = error;
      }

      return memo;
    }, errors);
  };
}
